import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { Input, InputField } from "@/components/ui/input";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import {
  getOrgQRControllerListQueryKey,
  useOrgQRControllerCreate,
  useOrgQRControllerDelete,
  useOrgQRControllerUpdate,
} from "@/generated/api/api";
import { OrganizationQRDTO } from "@/generated/api/models";
import { useQRCodeDownloader } from "@/utils/useQRCode";
import { useToast } from "@/utils/useToast";
import { InterfaceAlertDialogProps } from "@gluestack-ui/alert-dialog/lib/types";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, Download, Share } from "lucide-react-native";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Keyboard, Platform } from "react-native";
import QRCodeStyled from "react-native-qrcode-styled";
import { z } from "zod";

const QRCodeSchema = z.object({
  internalCode: z.string().min(1, "Internal code is required"),
  displayName: z.string().optional(),
  collectingGoal: z.string().optional(),
});

type QRCodeSchemaType = z.infer<typeof QRCodeSchema>;

export function RestaurantQRCodesModalCreate(
  props: { organizationId: string } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >
) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QRCodeSchemaType>({
    resolver: zodResolver(QRCodeSchema),
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useOrgQRControllerCreate({
    mutation: {
      onSuccess: (data) => {
        showToast("success", "QR-code has been successfully created");
        queryClient.setQueryData(
          getOrgQRControllerListQueryKey(props.organizationId),
          (old: OrganizationQRDTO[] | undefined) => {
            const result = [...(old ?? []), data];

            return result;
          }
        );
        props.onClose?.();
        reset();
      },
    },
  });

  const onSubmit = (data: QRCodeSchemaType) => {
    return mutateAsync({
      organizationId: props.organizationId,
      data,
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Create QR-Code</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl
            isInvalid={!!errors?.internalCode}
            className="w-full"
            isRequired
          >
            <FormControlLabel>
              <FormControlLabelText>Internal code</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="internalCode"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Internal code for your convenience"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Please put something meaningful to you, e.g. Bar Jar.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.internalCode?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.displayName} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Display name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="displayName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Display name on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Shows to tippers. If not set - will be displayed business name.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.displayName?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.collectingGoal} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Collecting goal</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="collectingGoal"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Collecting goal on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                You may put something meaningful here for tippers, which would
                make them to contribute to collecting goal.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.collectingGoal?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Create</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RestaurantQRCodesModalEdit(
  props: { organizationId: string; qr: OrganizationQRDTO } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >
) {
  const defaultValues = useMemo(
    () => ({
      internalCode: props.qr.internalCode,
      displayName: props.qr.displayName ?? undefined,
      collectingGoal: props.qr.collectingGoal ?? undefined,
    }),
    [props.qr.internalCode, props.qr.displayName, props.qr.collectingGoal]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QRCodeSchemaType>({
    resolver: zodResolver(QRCodeSchema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [props.qr]);

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutateAsync } = useOrgQRControllerUpdate({
    mutation: {
      onSuccess: (data) => {
        showToast("success", "QR-code has been successfully updated");
        queryClient.setQueryData(
          getOrgQRControllerListQueryKey(props.organizationId),
          (old: OrganizationQRDTO[] | undefined) => {
            if (!old) {
              return;
            }

            const index = old.findIndex((p) => p.id === data.id);

            if (index === -1) {
              queryClient.invalidateQueries({
                queryKey: getOrgQRControllerListQueryKey(props.organizationId),
              });

              return;
            }

            const copy = [...old];
            copy[index] = data;

            return copy;
          }
        );
        reset({
          internalCode: data.internalCode,
          displayName: data.displayName ?? undefined,
          collectingGoal: data.collectingGoal ?? undefined,
        });
        props.onClose?.();
        reset();
      },
    },
  });

  const onSubmit = (data: QRCodeSchemaType) => {
    return mutateAsync({
      organizationId: props.organizationId,
      qrId: props.qr.id,
      data,
    }).catch(() => {});
  };

  const handleKeyPress = () => {
    Keyboard.dismiss();
    handleSubmit(onSubmit)();
  };

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Update QR-Code</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl
            isInvalid={!!errors?.internalCode}
            className="w-full"
            isRequired
          >
            <FormControlLabel>
              <FormControlLabelText>Internal code</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="internalCode"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Internal code for your convenience"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Please put something meaningful to you, e.g. Bar Jar.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.internalCode?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.displayName} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Display name</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="displayName"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Display name on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Shows to tippers. If not set - will be displayed business name.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.displayName?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.collectingGoal} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Collecting goal</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue=""
              name="collectingGoal"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    placeholder="Collecting goal on QR-code page"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={handleKeyPress}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                You may put something meaningful here for tippers, which would
                make them to contribute to collecting goal.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.collectingGoal?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RestaurantQRCodesModalDelete({
  organizationId,
  qr,
  ...rest
}: { organizationId: string; qr: OrganizationQRDTO } & Pick<
  InterfaceAlertDialogProps,
  "isOpen" | "onClose"
>) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { mutate, isPending } = useOrgQRControllerDelete({
    mutation: {
      onSuccess: (data, variables, context) => {
        showToast("success", "QR-code has been successfully deleted");
        queryClient.setQueryData(
          getOrgQRControllerListQueryKey(organizationId),
          (old: OrganizationQRDTO[] | undefined) => {
            return old?.filter((oldQr) => oldQr.id !== qr.id);
          }
        );
        rest.onClose?.();
      },
    },
  });

  return (
    <AlertDialog {...rest}>
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading className="text-typography-950 font-semibold" size="md">
            Are you sure you want to delete QR-code {qr.internalCode}?
          </Heading>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3 mb-4">
          <Text size="sm">
            After delete, this QR-code will be unavailable for receiving tips.
          </Text>
        </AlertDialogBody>
        <AlertDialogFooter className="">
          <Button
            variant="outline"
            action="secondary"
            onPress={rest.onClose}
            size="sm"
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            size="sm"
            isDisabled={isPending}
            onPress={() => mutate({ organizationId, qrId: qr.id })}
          >
            <ButtonText>Delete</ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RestaurantQRCodesModalShowCode({
  qr,
  ...props
}: { organizationId: string; qr: OrganizationQRDTO } & Pick<
  InterfaceModalProps,
  "isOpen" | "onClose"
>) {
  const { qrCodeRef, onCopy, onShare, link } = useQRCodeDownloader({
    qr,
  });

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>{qr.internalCode}</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody contentContainerClassName="items-center">
          <QRCodeStyled
            ref={qrCodeRef}
            data={link}
            padding={20}
            pieceSize={8}
          />
        </ModalBody>
        <ModalFooter className="flex-col items-center">
          <HStack className="gap-4">
            <Button onPress={onCopy} variant="outline">
              <ButtonIcon as={Copy} size={20} className="text-typography-950" />
              <ButtonText>Copy link</ButtonText>
            </Button>
            <Button onPress={onShare}>
              <ButtonIcon as={Platform.OS === "web" ? Download : Share} />
              <ButtonText>
                {Platform.OS === "web" ? "Download PNG" : "Share"}
              </ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
